/**
 * Universal mantra audio registry (Phase 3B).
 * IDs map to public assets under /audio/mantras/*.wav
 */
export const MANTRA_AUDIO_REGISTRY = {
  om: "/audio/mantras/om.wav",
  om_namah_shivaya: "/audio/mantras/om_namah_shivaya.wav",
  gayatri: "/audio/mantras/gayatri.wav",
  mahamrityunjaya: "/audio/mantras/mahamrityunjaya.wav",
  om_gam_ganapataye: "/audio/mantras/om_gam_ganapataye.wav",
  om_ramaya_namah: "/audio/mantras/om_ramaya_namah.wav",
};

/** Devanagari / transliteration → registry id */
const TEXT_TO_ID = [
  ["ॐ नमः शिवाय", "om_namah_shivaya"],
  ["ॐ नमः शिवाय", "om_namah_shivaya"],
  ["ॐ", "om"],
  ["ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि धियो यो नः प्रचोदयात्", "gayatri"],
  ["ॐ त्र्यम्बकं यजामहे सुगन्धिं पुष्टिवर्धनम्", "mahamrityunjaya"],
  ["ॐ गं गणपतये नमः", "om_gam_ganapataye"],
  ["ॐ रामाय नमः", "om_ramaya_namah"],
  ["ॐ हनुमते नमः", "om_ramaya_namah"],
];

const DEVANAGARI = /[\u0900-\u097F]/;

export function hasDevanagari(text) {
  return typeof text === "string" && DEVANAGARI.test(text);
}

function normalizeKey(s) {
  return String(s ?? "")
    .toLowerCase()
    .replace(/[^\w\u0900-\u097F]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Resolve mantra registry id from id, transliteration, or Sanskrit text.
 */
export function resolveMantraId(textOrId) {
  const raw = String(textOrId ?? "").trim();
  if (!raw) return null;

  const snake = normalizeKey(raw);
  if (MANTRA_AUDIO_REGISTRY[snake]) return snake;

  const compact = raw.replace(/\s+/g, " ").trim();
  for (const [text, id] of TEXT_TO_ID) {
    if (compact.includes(text) || text.includes(compact)) return id;
  }

  if (/namah.*shiv/i.test(raw) || /शिवाय/.test(raw)) return "om_namah_shivaya";
  if (/gayatri|गायत्री|सवितु/i.test(raw)) return "gayatri";
  if (/mahamrityunjaya|महामृत्यु/i.test(raw)) return "mahamrityunjaya";
  if (/ganapat|गणपत/i.test(raw)) return "om_gam_ganapataye";
  if (/रामाय|ramaya/i.test(raw)) return "om_ramaya_namah";
  if (raw === "ॐ" || /^om$/i.test(raw)) return "om";

  return null;
}

export function publicMantraUrl(mantraId) {
  const file = MANTRA_AUDIO_REGISTRY[mantraId];
  return file || null;
}

/** Bīja and seed syllables often require guru upadeśa. */
export function isBijaMantra(text) {
  const s = String(text ?? "");
  if (/\b(bija|बीज)\b/i.test(s)) return true;
  if (/ॐ\s*[गरलयहवशषस]/u.test(s)) return true;
  if (/\b(gam|ganapat|लं|lam|रं|ram|यं|yam|हं|ham)\b/i.test(s) && s.length < 40) return true;
  return false;
}
